/**
 * M03 方案管理 fixture · S2 Day 7b · CLAUDE.md §11.5.1 数据工厂复用
 *
 * 注册时机：apps/admin/src/main.tsx 启动时调用 registerM03Fixtures()
 *
 * 设计意图：
 * - skills_catalog 11 SK 全收录 · 与后端 runtime registry 字段对齐
 * - list 含 7 PB · 覆盖现有全部处理方案和淘宝吸收落点
 * - {pb_id}/yaml 7 个 PB 均命中
 * - routing_table 是 P11 P1 主路由事实源，intent_mapping 是未命中时兜底
 * - validate-prefetch 三态：valid / 语法错 / schema 错
 */

import { registerFixture } from "./registry";
import type { MockResponse } from "./types";

interface SkillItem {
	skill_id: string;
	name: string;
	description: string;
	category: "shopify_admin" | "rag_qa" | "handoff" | "outreach" | "utility";
}

const MOCK_SKILLS: SkillItem[] = [
	{
		skill_id: "SK01",
		name: "订单列表定位",
		description: "按订单号、订单名或邮箱定位候选订单。",
		category: "shopify_admin",
	},
	{
		skill_id: "SK02",
		name: "订单详情聚合",
		description: "读取订单履约、物流与退款/取消辅证。",
		category: "shopify_admin",
	},
	{
		skill_id: "SK03",
		name: "商品详情获取",
		description: "读取商品详情、变体选项和售前对比所需事实。",
		category: "shopify_admin",
	},
	{
		skill_id: "SK04",
		name: "商品推荐",
		description: "基于 queryKeys 返回商品推荐候选，服务找同款、库存替代和售前推荐。",
		category: "rag_qa",
	},
	{
		skill_id: "SK05",
		name: "知识检索",
		description: "检索政策、FAQ、商品知识或运营知识库内容。",
		category: "rag_qa",
	},
	{
		skill_id: "SK06",
		name: "优惠规则查询",
		description: "查询优惠码、折扣规则与适用约束。",
		category: "shopify_admin",
	},
	{
		skill_id: "SK07",
		name: "取消订单",
		description: "取消订单写操作，需确认、dry-run 和质量门保护。",
		category: "shopify_admin",
	},
	{
		skill_id: "SK08",
		name: "退货创建",
		description: "创建退货写操作，需确认、dry-run 和质量门保护。",
		category: "shopify_admin",
	},
	{
		skill_id: "SK09",
		name: "退款执行",
		description: "执行退款写操作，需确认、dry-run 和质量门保护。",
		category: "shopify_admin",
	},
	{
		skill_id: "SK10",
		name: "修改地址",
		description: "修改收货地址写操作，需确认、dry-run 和质量门保护。",
		category: "shopify_admin",
	},
	{
		skill_id: "SK11",
		name: "转人工",
		description: "转人工处理，保留上下文摘要。",
		category: "handoff",
	},
];

interface PbListItem {
	playbook_id: string;
	name: string;
	version: string;
	intent_bindings: string[];
	allowed_skills_count: number;
	step_budget: number;
	has_pre_fetch: boolean;
	branch_count: number;
	branch_names: string[];
}

const MOCK_PB_LIST: PbListItem[] = [
	{
		playbook_id: "PB01_order_query",
		name: "订单查询方案",
		version: "V0.1",
		intent_bindings: ["order_status_inquiry", "shipping_tracking"],
		allowed_skills_count: 4,
		step_budget: 4,
		has_pre_fetch: true,
		branch_count: 0,
		branch_names: [],
	},
	{
		playbook_id: "PB02_refund",
		name: "退款办理方案",
		version: "V0.1",
		intent_bindings: ["refund_request"],
		allowed_skills_count: 5,
		step_budget: 5,
		has_pre_fetch: true,
		branch_count: 4,
		branch_names: ["主观退", "客观退", "部分退", "店铺余额退款"],
	},
	{
		playbook_id: "PB03_return",
		name: "退货办理方案",
		version: "V0.1",
		intent_bindings: ["return_request"],
		allowed_skills_count: 5,
		step_budget: 5,
		has_pre_fetch: true,
		branch_count: 8,
		branch_names: ["标准退", "自寄", "换货", "不可退品类", "已损坏", "错件", "缺件", "包裹丢失"],
	},
	{
		playbook_id: "PB04_cancel",
		name: "取消订单方案",
		version: "V0.1",
		intent_bindings: ["order_cancel"],
		allowed_skills_count: 5,
		step_budget: 4,
		has_pre_fetch: true,
		branch_count: 0,
		branch_names: [],
	},
	{
		playbook_id: "PB05_address_change",
		name: "修改地址方案",
		version: "V0.1",
		intent_bindings: ["address_change"],
		allowed_skills_count: 5,
		step_budget: 4,
		has_pre_fetch: true,
		branch_count: 0,
		branch_names: [],
	},
	{
		playbook_id: "PB06_pre_sale",
		name: "售前商品方案",
		version: "V0.1",
		intent_bindings: ["product_inquiry", "product_recommendation"],
		allowed_skills_count: 5,
		step_budget: 5,
		has_pre_fetch: true,
		branch_count: 5,
		branch_names: ["商品事实", "商品对比", "找同款", "库存替代", "弃单挽回"],
	},
	{
		playbook_id: "PB07_discount",
		name: "优惠咨询方案",
		version: "V0.1",
		intent_bindings: ["discount_inquiry"],
		allowed_skills_count: 3,
		step_budget: 4,
		has_pre_fetch: true,
		branch_count: 0,
		branch_names: [],
	},
];

const MOCK_PB01_YAML = `playbook_id: PB01_order_query
name: "订单查询方案"
version: "V0.1"
intent_bindings:
  - order_status_inquiry
  - shipping_tracking
recommended_skills: [SK01, SK02]
allowed_skills: [SK01, SK02, SK05, SK11]
step_budget: 4
pre_fetch_spec:
  auto_invoke_if_extractable:
    - skill_id: SK01
      trigger_slots: [order_id, email, order_name]
      trigger_mode: any
    - skill_id: SK02
      trigger_slots: [order_id]
      trigger_mode: all
  rag_namespaces: [policy, faq]
  top_k: 5
guidance: |
  ## PB01 订单查询方案
  - 查询订单、物流和履约状态。
  - 多订单时先让客户选择，不替客户猜。
fallback_hint: |
  - 未定位订单时追问订单号或邮箱。
`;

const MOCK_PB02_YAML = `playbook_id: PB02_refund
name: "退款办理方案"
version: "V0.1"
intent_bindings:
  - refund_request
recommended_skills: [SK01, SK02, SK09]
allowed_skills: [SK01, SK02, SK05, SK09, SK11]
step_budget: 5
pre_fetch_spec:
  auto_invoke_if_extractable:
    - skill_id: SK01
      trigger_slots: [order_id, email]
      trigger_mode: any
  rag_namespaces: [policy, faq]
  top_k: 5
branches:
  subjective:
    display_name: 主观退
    sub_tasks: [主观退]
    allowed_skills: [SK01, SK02, SK05, SK09, SK11]
  objective:
    display_name: 客观退
    sub_tasks: [客观退]
    allowed_skills: [SK01, SK02, SK05, SK09, SK11]
  partial:
    display_name: 部分退
    sub_tasks: [部分退]
    allowed_skills: [SK01, SK02, SK05, SK09, SK11]
  store_credit:
    display_name: 店铺余额退款
    sub_tasks: [store_credit]
    allowed_skills: [SK01, SK02, SK05, SK09, SK11]
guidance: |
  ## PB02 退款办理方案（V0.1 MVP）
  - 受理：订单可识别
  - 决策：按客户诉求
  - 执行：SK09 调用
  - 升级：失败转 SK11
  - 完成：用户确认
fallback_hint: |
  - SK01 未定位 → 追问 order_id / email
  - SK09 失败 → 切 refundMethod 重试 1 次 → SK11
`;

const MOCK_PB05_YAML = `playbook_id: PB05_address_change
name: "修改地址方案"
version: "V0.1"
intent_bindings:
  - address_change
recommended_skills: [SK01, SK02, SK10]
allowed_skills: [SK01, SK02, SK05, SK10, SK11]
step_budget: 4
pre_fetch_spec:
  auto_invoke_if_extractable:
    - skill_id: SK01
      trigger_slots: [order_id]
      trigger_mode: any
    - skill_id: SK02
      trigger_slots: [order_id]
      trigger_mode: all
  rag_namespaces: [policy, faq]
  top_k: 4
guidance: |
  ## PB05 修改地址方案
  - 受理：订单可识别 + 未发货
  - 决策：完整核对地址后才允许 SK10 dry-run / 受保护写操作
  - 执行：客户确认后进入写操作保护链
  - 升级：已发货 → SK11
  - 完成：用户确认
fallback_hint: |
  - SK01 未定位 → 追问
  - SK10 失败 → SK11
`;

const MOCK_PB06_YAML = `playbook_id: PB06_pre_sale
name: "售前商品方案"
version: "V0.1"
intent_bindings:
  - product_inquiry
  - product_recommendation
recommended_skills: [SK03]
allowed_skills: [SK03, SK04, SK05, SK06, SK11]
step_budget: 5
pre_fetch_spec:
  auto_invoke_if_extractable:
    - skill_id: SK03
      trigger_slots: [product_id, current_product_id, product_name, list_products]
      trigger_mode: any
  rag_namespaces: [product_kb, faq]
  top_k: 5
branches:
  product_fact:
    display_name: 商品事实
    sub_tasks: [factual, 商品事实, 库存, 下架]
    allowed_skills: [SK03, SK05, SK11]
  product_compare:
    display_name: 商品对比
    sub_tasks: [对比, 商品对比]
    allowed_skills: [SK03, SK05, SK11]
  find_similar:
    display_name: 找同款
    sub_tasks: [找同款, 新需求]
    allowed_skills: [SK03, SK04, SK05, SK11]
    pre_fetch_spec:
      auto_invoke_if_extractable:
        - skill_id: SK04
          trigger_slots: [queryKeys]
          trigger_mode: all
      rag_namespaces: [product_kb, faq]
      top_k: 5
  stock_alt:
    display_name: 库存替代
    sub_tasks: [库存替代, 库存售罄]
    allowed_skills: [SK03, SK04, SK05, SK11]
    pre_fetch_spec:
      auto_invoke_if_extractable:
        - skill_id: SK04
          trigger_slots: [queryKeys]
          trigger_mode: all
      rag_namespaces: [product_kb, faq]
      top_k: 5
  abandoned_recovery:
    display_name: 弃单挽回
    sub_tasks: [弃单挽回]
    allowed_skills: [SK03, SK05, SK06, SK11]
guidance: |
  ## PB06 售前商品方案
  - 受理：商品咨询、导购推荐、商品对比、找相似。
  - 决策：商品事实走 SK03；找同款 / 库存替代可在 queryKeys 满足时预取 SK04 推荐候选。
  - 执行：不编造商品属性，商品对比必须基于商品证据。
fallback_hint: |
  - 商品不明确 → 追问 SKU、品类或偏好。
  - SK03 空结果 → 用商品知识兜底并说明边界。
`;

const MOCK_PB03_YAML = `playbook_id: PB03_return
name: "退货办理方案"
version: "V0.1"
intent_bindings:
  - return_request
recommended_skills: [SK01, SK02, SK08]
allowed_skills: [SK01, SK02, SK05, SK08, SK11]
step_budget: 5
pre_fetch_spec:
  auto_invoke_if_extractable:
    - skill_id: SK01
      trigger_slots: [order_id, email, order_name]
      trigger_mode: any
    - skill_id: SK02
      trigger_slots: [order_id]
      trigger_mode: all
  rag_namespaces: [policy, faq]
  top_k: 5
branches:
  standard:
    display_name: 标准退
    sub_tasks: [标准退]
    allowed_skills: [SK01, SK02, SK05, SK08, SK11]
  self_ship:
    display_name: 自寄
    sub_tasks: [自寄]
    allowed_skills: [SK01, SK02, SK05, SK11]
  exchange:
    display_name: 换货
    sub_tasks: [换货]
    allowed_skills: [SK01, SK02, SK05, SK08, SK11]
  non_returnable:
    display_name: 不可退品类
    sub_tasks: [不可退品类]
    allowed_skills: [SK01, SK02, SK05, SK11]
  damaged:
    display_name: 已损坏
    sub_tasks: [已损坏]
    allowed_skills: [SK01, SK02, SK05, SK08, SK11]
  wrong_item:
    display_name: 错件
    sub_tasks: [错件]
    allowed_skills: [SK01, SK02, SK05, SK08, SK11]
  missing_item:
    display_name: 缺件
    sub_tasks: [缺件]
    allowed_skills: [SK01, SK02, SK05, SK11]
  package_lost:
    display_name: 包裹丢失
    sub_tasks: [包裹丢失]
    allowed_skills: [SK01, SK02, SK05, SK11]
guidance: |
  ## PB03 退货办理方案
  - 先确认订单和退货窗口，再进入受保护退货创建。
fallback_hint: |
  - 退货/退款混淆时先解释区别。
`;

const MOCK_PB04_YAML = `playbook_id: PB04_cancel
name: "取消订单方案"
version: "V0.1"
intent_bindings:
  - order_cancel
recommended_skills: [SK01, SK02, SK07]
allowed_skills: [SK01, SK02, SK05, SK07, SK11]
step_budget: 4
pre_fetch_spec:
  auto_invoke_if_extractable:
    - skill_id: SK01
      trigger_slots: [order_id, email, order_name]
      trigger_mode: any
    - skill_id: SK02
      trigger_slots: [order_id]
      trigger_mode: all
  rag_namespaces: [policy, faq]
  top_k: 4
guidance: |
  ## PB04 取消订单方案
  - 未发货订单才进入取消保护链，已发货转退货或人工。
fallback_hint: |
  - 客户未说明订单时追问或让客户选择近期订单。
`;

const MOCK_PB07_YAML = `playbook_id: PB07_discount
name: "优惠咨询方案"
version: "V0.1"
intent_bindings:
  - discount_inquiry
recommended_skills: [SK06]
allowed_skills: [SK05, SK06, SK11]
step_budget: 4
pre_fetch_spec:
  auto_invoke_if_extractable:
    - skill_id: SK06
      trigger_slots: [discount_code, promo_context]
      trigger_mode: any
  rag_namespaces: [policy, faq, product_kb]
  top_k: 4
guidance: |
  ## PB07 优惠咨询方案
  - 查询优惠码、当前活动、适用条件和叠加边界。
fallback_hint: |
  - 优惠码失效时直接说明，不编造可用优惠。
`;

const MOCK_PB_YAMLS: Record<string, { yaml: string; version: string; name: string }> = {
	PB01_order_query: { yaml: MOCK_PB01_YAML, version: "V0.1", name: "订单查询方案" },
	PB02_refund: { yaml: MOCK_PB02_YAML, version: "V0.1", name: "退款办理方案" },
	PB03_return: { yaml: MOCK_PB03_YAML, version: "V0.1", name: "退货办理方案" },
	PB04_cancel: { yaml: MOCK_PB04_YAML, version: "V0.1", name: "取消订单方案" },
	PB05_address_change: { yaml: MOCK_PB05_YAML, version: "V0.1", name: "地址修改方案" },
	PB06_pre_sale: { yaml: MOCK_PB06_YAML, version: "V0.1", name: "售前商品方案" },
	PB07_discount: { yaml: MOCK_PB07_YAML, version: "V0.1", name: "优惠咨询方案" },
};

const MOCK_INTENT_MAPPING_YAML = `version: "2026-04-28T00:00:00Z"
mappings:
  order_status_inquiry: [PB01_order_query]
  shipping_tracking: [PB01_order_query]
  order_issue: []
  refund_request: [PB02_refund]
  return_request: [PB03_return]
  order_cancel: [PB04_cancel]
  address_change: [PB05_address_change]
  product_inquiry: [PB06_pre_sale]
  product_recommendation: [PB06_pre_sale]
  discount_inquiry: [PB07_discount]
  knowledge_qa: []
  human_handoff: []
  unknown: []
`;

const MOCK_ROUTING_TABLE_YAML = `version: "2026-05-25T00:00:00Z"
fallback_to_intent_playbook_mapping: true
rules:
  - rule_id: rt_order_issue_damaged
    intent: order_issue
    sub_task: 已损坏
    pb_id: PB03_return
    branch: damaged
    priority: 80
    enabled: true
    description: 收到商品损坏时进入退货方案的已损坏分支。

  - rule_id: rt_order_issue_wrong_item
    intent: order_issue
    sub_task: 错件
    pb_id: PB03_return
    branch: wrong_item
    priority: 80
    enabled: true
    description: 错发商品进入退货方案的错件分支。

  - rule_id: rt_order_issue_missing_item
    intent: order_issue
    sub_task: 缺件
    pb_id: PB03_return
    branch: missing_item
    priority: 80
    enabled: true
    description: 少件/缺件进入退货方案的缺件分支。

  - rule_id: rt_order_issue_package_lost
    intent: order_issue
    sub_task: 包裹丢失
    pb_id: PB03_return
    branch: package_lost
    priority: 80
    enabled: true
    description: 包裹丢失不创建退货标签，进入退货方案的包裹丢失分支。

  - rule_id: rt_order_issue_default
    intent: order_issue
    sub_task: "*"
    pb_id: PB03_return
    priority: 20
    enabled: true
    description: 收货问题未命中明确子任务时进入退货方案默认路径。

  - rule_id: rt_refund_status_short_circuit
    intent: refund_request
    sub_task: 状态查询
    short_circuit_skill: SK02
    priority: 100
    enabled: true
    description: 仅查询退款状态时直接走订单详情聚合，不锁定退款办理 PB。

  - rule_id: rt_return_status_short_circuit
    intent: return_request
    sub_task: 状态查询
    short_circuit_skill: SK02
    priority: 100
    enabled: true
    description: 仅查询退货状态时直接走订单详情聚合，不锁定退货办理 PB。
`;

let currentMappingYaml = MOCK_INTENT_MAPPING_YAML;
let currentRoutingTableYaml = MOCK_ROUTING_TABLE_YAML;
const currentPbYamls = { ...MOCK_PB_YAMLS };

/**
 * 注册 M03 fixtures · 启动时调用一次
 */
export function registerM03Fixtures(): void {
	registerFixture({
		pathname: "/admin/v1/playbook/skills_catalog",
		methods: ["GET"],
		resolve: () => ({ data: { items: MOCK_SKILLS, total: MOCK_SKILLS.length } }),
	});

	registerFixture({
		pathname: "/admin/v1/playbook/list",
		methods: ["GET"],
		resolve: () => ({ data: { items: MOCK_PB_LIST, total: MOCK_PB_LIST.length } }),
	});

	registerFixture<unknown>({
		pathname: "/admin/v1/playbook/:pb_id/yaml",
		methods: ["GET", "PUT"],
		resolve: (ctx): MockResponse<unknown> => {
			const pbId = ctx.params.pb_id;
			if (!pbId) return { status: 404, data: { detail: "mock: pb_id missing" } };
			const existing = currentPbYamls[pbId];
			if (!existing) return { status: 404, data: { detail: `mock: ${pbId} not found` } };
			if (ctx.method === "GET") {
				return {
					data: {
						playbook_id: pbId,
						yaml_text: existing.yaml,
						parsed_version: existing.version,
						parsed_name: existing.name,
					},
				};
			}
			// PUT
			const body = ctx.body as { yaml_text?: string; expected_version?: string | null };
			if (!body?.yaml_text) {
				return { status: 400, data: { detail: "mock: yaml_text 必填" } };
			}
			if (body.expected_version != null && body.expected_version !== existing.version) {
				return {
					status: 409,
					data: {
						detail: `mock 乐观锁：expected=${body.expected_version} current=${existing.version}`,
					},
				};
			}
			// 提取 version 字段（粗匹配 · 不做完整 YAML 校验）
			const versionMatch = /version:\s*"?([^"\n]+)"?/m.exec(body.yaml_text);
			const newVersion = versionMatch ? versionMatch[1] || existing.version : existing.version;
			currentPbYamls[pbId] = {
				yaml: body.yaml_text,
				version: newVersion,
				name: existing.name,
			};
			return {
				data: {
					playbook_id: pbId,
					parsed_version: newVersion,
					cache_invalidated: true,
				},
			};
		},
	});

	registerFixture<unknown>({
		pathname: "/admin/v1/playbook/intent_mapping",
		methods: ["GET", "PUT"],
		resolve: (ctx): MockResponse<unknown> => {
			if (ctx.method === "GET") {
				const versionMatch = /version:\s*"?([^"\n]+)"?/m.exec(currentMappingYaml);
				return {
					data: {
						yaml_text: currentMappingYaml,
						bundle_version: versionMatch ? versionMatch[1] : "mock",
						intents_with_mapping: 9,
					},
				};
			}
			const body = ctx.body as { yaml_text?: string; expected_version?: string | null };
			if (!body?.yaml_text) {
				return { status: 400, data: { detail: "mock: yaml_text 必填" } };
			}
			currentMappingYaml = body.yaml_text;
			const versionMatch = /version:\s*"?([^"\n]+)"?/m.exec(body.yaml_text);
			return {
				data: {
					bundle_version: versionMatch ? versionMatch[1] : "mock",
					cache_invalidated: true,
					unknown_intents: [],
				},
			};
		},
	});

	registerFixture<unknown>({
		pathname: "/admin/v1/playbook/routing_table",
		methods: ["GET", "PUT"],
		resolve: (ctx): MockResponse<unknown> => {
			if (ctx.method === "GET") {
				const versionMatch = /version:\s*"?([^"\n]+)"?/m.exec(currentRoutingTableYaml);
				const shortCircuitRules = (currentRoutingTableYaml.match(/short_circuit_skill:/g) ?? [])
					.length;
				const branchRules = (currentRoutingTableYaml.match(/^\s+branch:/gm) ?? []).length;
				const enabledRules =
					(currentRoutingTableYaml.match(/\n\s+-\s+rule_id:/g) ?? []).length ||
					(currentRoutingTableYaml.match(/^-\s+rule_id:/gm) ?? []).length;
				return {
					data: {
						yaml_text: currentRoutingTableYaml,
						bundle_version: versionMatch ? versionMatch[1] : "mock",
						enabled_rules: enabledRules,
						short_circuit_rules: shortCircuitRules,
						branch_rules: branchRules,
						fallback_to_intent_playbook_mapping:
							/fallback_to_intent_playbook_mapping:\s*true/m.test(currentRoutingTableYaml),
					},
				};
			}
			const body = ctx.body as { yaml_text?: string; expected_version?: string | null };
			if (!body?.yaml_text) {
				return { status: 400, data: { detail: "mock: yaml_text 必填" } };
			}
			currentRoutingTableYaml = body.yaml_text;
			const versionMatch = /version:\s*"?([^"\n]+)"?/m.exec(body.yaml_text);
			return {
				data: {
					bundle_version: versionMatch ? versionMatch[1] : "mock",
					cache_invalidated: true,
					unknown_intents: [],
				},
			};
		},
	});

	registerFixture<unknown>({
		pathname: "/admin/v1/playbook/validate-prefetch",
		methods: ["POST"],
		resolve: (ctx): MockResponse<unknown> => {
			const body = ctx.body as { pre_fetch_spec_yaml?: string };
			const text = body?.pre_fetch_spec_yaml ?? "";
			if (!text.trim()) {
				return { data: { valid: false, parsed: null, errors: ["mock: pre_fetch_spec_yaml 必填"] } };
			}
			// 粗略 mock：含 trigger_mode: invalid → schema 错；含两个连续冒号 → 语法错
			if (/trigger_mode:\s*invalid/m.test(text)) {
				return {
					data: { valid: false, parsed: null, errors: ["trigger_mode: must be any or all"] },
				};
			}
			if (/:\s*:/m.test(text)) {
				return { data: { valid: false, parsed: null, errors: ["YAML 解析失败：mock"] } };
			}
			return {
				data: {
					valid: true,
					parsed: {
						auto_invoke_if_extractable: [],
						rag_namespaces: ["policy"],
						top_k: 5,
					},
					errors: [],
				},
			};
		},
	});
}
